import type { ConfigItems } from '@app/src/pages/Configurations/ConfigurationsScene'
import { ChevronRight, InfoOutlined } from '@mui/icons-material'
import { alpha, Box, Divider, List, ListItem, ListItemButton, ListItemText, Paper, Typography } from '@mui/material'
import React from 'react'
import { Helmet } from 'react-helmet'
import { useIntl } from 'react-intl'
import { generatePath, Link } from 'react-router-dom'

export type ConfigurationHubLandingProps = {
  hubTitleMessageId: string
  configItems: ConfigItems
  routePath: string
  emptyStateMessageId?: string
}

const ConfigurationHubLanding: React.FC<ConfigurationHubLandingProps> = ({
  hubTitleMessageId,
  configItems,
  routePath,
  emptyStateMessageId = 'configurations.hubLandingEmpty',
}) => {
  const { formatMessage } = useIntl()

  return (
    <>
      <Helmet>
        <title>{formatMessage({ id: hubTitleMessageId })}</title>
      </Helmet>

      <Box flexGrow={1} bgcolor="grey.100" pt={2} pb={4} px={4}>
        <Box mx="auto">
          <Box mb={2}>
            <Typography variant="h1" component="h1">
              {formatMessage({ id: hubTitleMessageId })}
            </Typography>
          </Box>

          <Paper elevation={0} sx={{ borderRadius: 2, overflow: 'hidden' }}>
            {configItems.length === 0 ? (
              <Box px={3} py={4}>
                <Typography color="text.secondary">{formatMessage({ id: emptyStateMessageId })}</Typography>
              </Box>
            ) : (
              <List disablePadding>
                {configItems.map((item, index) => {
                  const Icon = item.icon ?? InfoOutlined
                  const to = generatePath(routePath, { configurationsPage: item.configId })
                  return (
                    <React.Fragment key={item.configId}>
                      {index > 0 ? <Divider component="li" sx={{ mx: 3 }} /> : null}
                      <ListItem disablePadding>
                        <ListItemButton
                          component={Link}
                          to={to}
                          sx={{
                            py: 2.5,
                            px: 3,
                            alignItems: 'center',
                            gap: 2,
                            '&:hover': {
                              backgroundColor: 'grey.50',
                            },
                          }}
                        >
                          <Box
                            sx={({ palette }) => ({
                              width: 44,
                              height: 44,
                              borderRadius: '50%',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              flexShrink: 0,
                              bgcolor: alpha(palette.text.primary, 0.06),
                              color: 'text.secondary',
                            })}
                          >
                            <Icon />
                          </Box>
                          <ListItemText
                            primary={formatMessage({ id: item.titleMessageId ?? `configurations.${item.configId}` })}
                            secondary={formatMessage({ id: `configurations.landingDescriptions.${item.configId}` })}
                          />
                          <ChevronRight sx={{ color: 'action.active' }} />
                        </ListItemButton>
                      </ListItem>
                    </React.Fragment>
                  )
                })}
              </List>
            )}
          </Paper>
        </Box>
      </Box>
    </>
  )
}

export default ConfigurationHubLanding
